<script setup>
import {MinusIcon, PlusIcon} from "@heroicons/vue/20/solid";
import {computed} from "vue";
import {useGtm} from "@gtm-support/vue-gtm";
import {usePage} from "@inertiajs/vue3";

const emit = defineEmits(['update:modelValue', 'change']);

const locale = computed(() => usePage().props.locale);

const props = defineProps({
    modelValue: [Number, String],
    responsive: Boolean,
    allowZero: Boolean,
    min: Number,
    max: Number,
    variant: {
        type: String,
        default: "default",
        validator(value) {
            // The value must match one of these strings
            return ["default", "dense"].includes(value);
        },
    },
    disabled: Boolean,
    numberOfDecimals: {
        type: [Number, String],
        default: 0,
    },
});

const displayValue = computed(() => {
    if (locale.value === 'nl') {
        return String(parseFloat(props.modelValue).toFixed(props.numberOfDecimals))
            .replace(/[^\d.-]/g, '')
            .replace('.', ',');
    }
    return props.modelValue.toFixed(props.numberOfDecimals);
});

const emitValue = (value) => {
    value = value.target.value;

    let asString = String(value)
        .replace(',', '.')
        .replace(/[^\d.-]/g, '');

    let asFloat = parseFloat(asString);

    if (isNaN(asFloat)) {
        asFloat = 0;
    }

    asFloat = asFloat.toFixed(props.numberOfDecimals);

    if (props.min && asFloat < props.min) {
        asFloat = props.min;
    }

    if (props.max && asFloat > props.max) {
        asFloat = props.max;
    }

    // Toggle type so an update is triggered,
    // useful in the case where the user manually enters a forbidden value
    if (typeof props.modelValue === 'number') {
        asFloat = String(asFloat);
    } else {
        asFloat = parseFloat(asFloat);
    }

    asFloat = Math.max(0, asFloat);

    emit('update:modelValue', asFloat);
    emit('change', asFloat);

    if (parseFloat(asFloat) !== parseFloat(props.modelValue)) {
        useGtm().trackEvent({event: "addtocart"});
    }

};

function increment() {
    emitValue({target: {value: parseFloat(props.modelValue) + 1}});
}

function subtract() {
    emitValue({target: {value: parseFloat(props.modelValue) - 1}});
}
</script>
<template>
    <div
        v-if="['dense'].includes(variant)"
        class="flex items-center rounded-full border-0 bg-lightGray-300 p-0 text-center text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600"
    >
        <button
            v-if="!disabled"
            class="flex size-7 items-center justify-center rounded-full bg-primary-400 text-2xl text-white md:left-auto"
            @click="subtract"
        >
            <minus-icon class="size-5 text-black" />
        </button>
        <input
            :disabled="disabled"
            :value="displayValue"
            class="form-input size-8 border-0 bg-transparent p-0 text-center sm:w-10"
            type="number"
            @blur="emitValue"
            @change="emitValue"
        >
        <button
            v-if="!disabled"
            class="flex size-7 items-center justify-center rounded-full bg-primary-400 text-base text-white md:left-auto"
            @click="increment"
        >
            <plus-icon class="size-5 text-black" />
        </button>
    </div>

    <div
        v-else
        class="flex items-center gap-2"
    >
        <button
            v-if="!disabled"
            class="flex size-9 items-center justify-center rounded-full bg-primary-400 text-2xl text-white sm:size-10 md:left-auto"
            @click="subtract"
        >
            <minus-icon class="size-7 text-black" />
        </button>
        <input
            :disabled="disabled"
            :value="displayValue"
            class="form-input h-9 w-24 rounded-full border-0 text-center text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 disabled:bg-gray-50 sm:h-10 sm:w-32"
            type="number"
            @blur="emitValue"
            @change="emitValue"
        >
        <button
            v-if="!disabled"
            class="flex size-9 items-center justify-center rounded-full bg-primary-400 text-base text-white sm:size-10 md:left-auto"
            @click="increment"
        >
            <plus-icon class="size-7 text-black" />
        </button>
    </div>
</template>

<style scoped>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
</style>
